// frontend/src/components/BookReading/BookList.js
//#region Imports
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Badge, Button, ProgressBar } from 'react-bootstrap';
import { FaSeedling, FaBookOpen, FaPlay, FaLock } from 'react-icons/fa';
import { BookReadingContext } from './BookReading';
import { AuthContext } from '../../contexts/AuthContext';
import HiddenItemNotificationModal from './HiddenItemNotificationModal';
import {
  getReadChapterIds,
  getLastRead,
  bookProgress,
  coverGradient,
} from './readingProgress';
//#endregion

function BookList() {
  //#region Declarações
  const navigate = useNavigate();
  const {
    books,
    setSelectedBook,
    translations: t,
  } = useContext(BookReadingContext);
  const { username } = useContext(AuthContext);

  const [showHiddenNotification, setShowHiddenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const readSet = getReadChapterIds(username);
  const lastRead = getLastRead(username);
  //#endregion

  //#region Efeitos
  // Se houver apenas um livro disponível, abre o sumário direto (sem estante)
  useEffect(() => {
    const visibleBooks = (books || []).filter((b) => b.visible);
    if (visibleBooks.length === 1) {
      const only = visibleBooks[0];
      setSelectedBook(only);
      navigate(`book/${only.id}`, { replace: true });
    }
  }, [books, navigate, setSelectedBook]);
  //#endregion

  //#region Funções handle
  const handleBookSelect = (book) => {
    setSelectedBook(book);
    navigate(`book/${book.id}`);
  };

  const handleContinue = (e, book) => {
    e.stopPropagation();
    setSelectedBook(book);
    if (lastRead && Number(lastRead.bookId) === Number(book.id) && lastRead.chapterId) {
      navigate(`chapter/${lastRead.chapterId}`);
    } else {
      navigate(`book/${book.id}`);
    }
  };

  const handleHiddenItemClick = (book) => {
    setNotificationMessage(t.bookInEditingMessage.replace('{title}', book.title));
    setShowHiddenNotification(true);
  };
  //#endregion

  //#region Renderização
  if (!books || books.length === 0) {
    return (
      <div className="text-center py-5">
        <FaBookOpen size={42} className="text-muted mb-3" />
        <h5>{t.noBooks}</h5>
        <p className="text-muted">{t.noBooksDescription}</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted mb-4">{t.libraryLead}</p>

      <Row xs={1} sm={2} lg={3} className="g-4">
        {books.map((book) => {
          // Livro oculto / em edição
          if (!book.visible) {
            return (
              <Col key={book.id}>
                <div
                  className="book-card book-card--locked"
                  onClick={() => handleHiddenItemClick(book)}
                >
                  <div className="book-cover" style={{ background: coverGradient(book) }}>
                    <FaSeedling className="book-cover-icon" />
                    <span className="book-cover-title">{book.title}</span>
                    <span className="book-lock"><FaLock /></span>
                  </div>
                  <div className="book-card-body">
                    <h3 className="book-card-title">{book.title}</h3>
                    <div className="book-card-meta">
                      {book.chapters.length} {t.chapters}
                    </div>
                    <Badge bg="secondary" className="align-self-start">{t.inEditing}</Badge>
                  </div>
                </div>
              </Col>
            );
          }

          const prog = bookProgress(book, readSet);
          const isContinue =
            lastRead && Number(lastRead.bookId) === Number(book.id) && prog.read > 0;

          return (
            <Col key={book.id}>
              <div
                className="book-card"
                role="button"
                tabIndex={0}
                onClick={() => handleBookSelect(book)}
                onKeyDown={(e) => e.key === 'Enter' && handleBookSelect(book)}
              >
                <div className="book-cover" style={{ background: coverGradient(book) }}>
                  <FaSeedling className="book-cover-icon" />
                  <span className="book-cover-title">{book.title}</span>
                </div>
                <div className="book-card-body">
                  <h3 className="book-card-title">{book.title}</h3>
                  <div className="book-card-meta">
                    {book.chapters.length} {t.chapters}
                    {prog.read > 0 && <> · {prog.pct}% {t.readLabel}</>}
                  </div>
                  {prog.read > 0 && (
                    <ProgressBar now={prog.pct} className="book-progress" />
                  )}
                  <Button
                    variant="brand"
                    size="sm"
                    className="book-cta mt-auto"
                    onClick={(e) => handleContinue(e, book)}
                  >
                    {isContinue ? (
                      <><FaPlay className="me-2" />{t.continueLabel}</>
                    ) : (
                      <><FaBookOpen className="me-2" />{t.startReading}</>
                    )}
                  </Button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      <HiddenItemNotificationModal
        show={showHiddenNotification}
        onHide={() => setShowHiddenNotification(false)}
        message={notificationMessage}
        translations={t}
      />
    </div>
  );
  //#endregion
}

export default BookList;
